import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from '../../../../common/withRouter';
import {
  DataDefinitionList,
  Tabs,
  EditableArea,
  Notification,
  IconWithText,
  Loader,
  FormikInput,
  Message
} from '../../../../web.common';
import * as Yup from 'yup';
import { sentenceCase } from 'change-case';
import { getApiErrorMessage } from '../../../../common/utils';
import withApi from '../../../higherOrderComponents/withApi';
import { DisplayGuid, DisplayDate, DisplayType } from '../../../items';
import { EditDisables } from './EditDisables';
import { IntegrationNextImport } from './IntegrationNextImport';

class IntegrationInternal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      integrationData: null,
      systemTypes: null,
      systemTypeParameters: null
    };

    this.mapParameters = this.mapParameters.bind(this);
    this.mapSystemParameters = this.mapSystemParameters.bind(this);
  }

  async componentDidMount() {
    const {
      props: {
        productId,
        loadData,
        api: {
          integrations: {
            getIntegrationByProductId,
            getSystemTypeParameters,
            getSystemTypes
          },
          proxies: { getProxies }
        }
      }
    } = this;

    loadData(productId);
    const integrationData = await getIntegrationByProductId(productId);
    console.log('integrationData', integrationData);
    const systemType = integrationData.SystemType;
    console.log('systemType', systemType);
    const systemTypes = await getSystemTypes(); // TODO: use redux to store the system types
    console.log('systemTypes', systemTypes);
    const systemTypeParameters = await getSystemTypeParameters(systemType);
    console.log('systemTypeParameters', systemTypeParameters);
    const mappedSystemTypeParameters = systemTypeParameters.reduce(
      (k, v) => ({ ...k, [v]: '' }),
      {}
    );
    console.log('mappedSystemTypeParameters', mappedSystemTypeParameters);
    const mappedSystemTypes = systemTypes.map(({ Name }) => ({
      value: Name,
      label: Name
    }));
    console.log('mappedSystemTypes', mappedSystemTypes);
    const proxyOptions = [
      { value: '', label: 'No proxy' },
      ...(await getProxies()).map(({ id, name }) => ({ value: id, label: name }))
    ];
    this.setState({
      proxyOptions,
      integrationData,
      systemTypeParameters,
      mappedSystemTypeParameters,
      mappedSystemTypes,
      SystemType: systemType,
      isLoading: false
    });
  }

  async onValidationError({ validateForm }) {
    const validationErrors = await validateForm();
    const fieldsWithError = Object.keys(validationErrors)
      .map(x => sentenceCase(x))
      .join(', ');
    this.setState({
      apiResponse: new Message(
        'error',
        `Unable to save. Check fields: ${fieldsWithError}`
      )
    });
  }

  mapSystemParameters(type) {
    this.setState({ isLoading: true }, async () => {
      const {
        props: {
          api: {
            integrations: { getSystemTypeParameters }
          }
        }
      } = this;
      const systemTypeParameters = await getSystemTypeParameters(type);
      const mappedSystemTypeParameters = systemTypeParameters.reduce(
        (k, v) => ({ ...k, [v]: '' }),
        {}
      );

      this.setState({
        systemTypeParameters,
        mappedSystemTypeParameters,
        SystemType: type,
        isLoading: false
      });
    });
  }

  mapParameters(isEditMode, rawValues = false) {
    const {
      integrationData,
      mappedSystemTypeParameters,
      systemTypeParameters,
      proxyOptions
    } = this.state;

    const mergedParameters = integrationData.Parameters
      ? { ...mappedSystemTypeParameters, ...integrationData.Parameters }
      : mappedSystemTypeParameters;

    return Object.fromEntries(
      Object.entries(mergedParameters)
        .map(([k, v]) => {
          // The Proxy parameter holds the id of a row of the proxy table.
          if (k === 'Proxy') {
            return [
              k,
              isEditMode ? (
                <FormikInput type='select' key={k} name={k} description={k} options={proxyOptions} />
              ) : rawValues ? (
                v
              ) : (
                (proxyOptions.find(({ value }) => value === v) || { label: v }).label
              )
            ];
          }

          return [
            k,
            isEditMode ? (
              <FormikInput type='text' key={k} name={k} description={k} />
            ) : (
              v
            )
          ];
        })
        .filter(item => systemTypeParameters.includes(item[0]))
    );
  }

  showDisables(disables) {
    return !disables ? (
      ''
    ) : (
      <ul>
        {disables.map(({ DisableFrom, DisableTo }) => (
          <li>
            <span style={{ marginLeft: '5px' }}>
              <DisplayDate>{DisableFrom}</DisplayDate>
              {' '}
              -
              {' '}
              <DisplayDate>{DisableTo}</DisplayDate>
              {' '}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  saveEditedIntegration(receivedProps) {
    this.setState({ isLoading: true }, async () => {
      const {
        props: {
          productId,
          language,
          api: {
            integrations: { editIntegrationData, saveNewIntegration }
          }
        },
        state: { systemTypeParameters }
      } = this;

      let updatedIntegrationData = null;
      let apiResponse;
      const editedParameters = Object.fromEntries(
        Object.entries(receivedProps.values).filter(item =>
          systemTypeParameters.includes(item[0])
        )
      );

      if (receivedProps.values.Id !== 0) {
        try {
          updatedIntegrationData = await editIntegrationData(
            receivedProps.values,
            productId,
            editedParameters
          );
          apiResponse = new Message(
            'success',
            'Integration updated successfully'
          );
        }
        catch (error) {
          this.setState({
            isLoading: false,
            apiResponse: getApiErrorMessage(error, language)
          });

          return;
        }
      }
      else {
        try {
          updatedIntegrationData = await saveNewIntegration(
            receivedProps.values,
            productId,
            editedParameters
          );
          apiResponse = new Message(
            'success',
            'Integration created successfully'
          );
        }
        catch (error) {
          this.setState({
            isLoading: false,
            apiResponse: getApiErrorMessage(error, language)
          });

          return;
        }

        //reload data in parent component so that integration buttons show up.
        this.props.loadData();
      }

      this.setState({
        isLoading: false,
        integrationData: updatedIntegrationData,
        apiResponse
      });

      receivedProps.toggleAreaEditMode();
    });
  }

  render() {
    const {
      isLoading,
      apiResponse,
      integrationData,
      SystemType,
      mappedSystemTypes
    } = this.state;

    if (isLoading) return <Loader />;

    const integrationParams = this.mapParameters(false, true);
    const {
      Id = 0,
      DaysToCrawl = '',
      NextImportAt = '',
      SpotIndexMatch = '',
      SpotTitleMatch = '',
      TimeZone = '',
      CreatedAt = '',
      Schedule = '',
      Comment = '',
      ActivitySplitCount = '',
      Disables = []
    } = integrationData || {};

    
    return (
      <React.Fragment>
        <Notification newMessage={apiResponse} />
        {integrationData ? (
          <EditableArea
            ariaEditorName='editor-name'
            onSubmit={receivedProps =>
              this.saveEditedIntegration(receivedProps)
            }
            savingText='Updating...'
            displayButtonLabels
            onValidationError={formikProps =>
              this.onValidationError(formikProps)
            }
            validationSchema={Yup.object().shape({
              SystemType: Yup.string().required('Field is Required'),
              SpotTitleMatch: Yup.string().required('Field is Required'),
              TimeZone: Yup.string().required('Field is Required'),
              Comment: Yup.string()
            })}
            initialValues={{
              Id,
              SpotTitleMatch,
              SystemType,
              TimeZone,
              CreatedAt,
              Comment,
              Disables,
              ...integrationParams
            }}
          >
            {({ isEditMode }) => (
              <React.Fragment>
                <Tabs tablistAriaTitle='integration'>
                  {[
                    {
                      tab: {
                        content: (
                          <IconWithText iconName='info' text='Main Info' />
                        )
                      },
                      panel: {
                        content: (
                          <DataDefinitionList
                            classNames={['border-0', 'detail-page-list']}
                            listData={[
                              {
                                Id: <DisplayGuid>{Id}</DisplayGuid>,
                                'System Type':
                                isEditMode ? (
                                  <FormikInput
                                    key='SystemType'
                                    type='select'
                                    options={mappedSystemTypes}
                                    onChange={type =>
                                      this.mapSystemParameters(type.value)
                                    }
                                    name='SystemType'
                                    description='SystemType'
                                    placeholder
                                  />
                                ) :
                                (
                                 <DisplayType>{SystemType}</DisplayType>
                                ),
                                'Spot Title Match': isEditMode ? (
                                  <FormikInput
                                    key='SpotTitleMatch'
                                    type='text'
                                    name='SpotTitleMatch'
                                    description='SpotTitleMatch'
                                  />
                                ) : (
                                  SpotTitleMatch
                                ),
                                'Time Zone': isEditMode ? (
                                  <FormikInput
                                    key='TimeZone'
                                    type='select'
                                    defaultValue={{ label: 'Europe/Copenhagen', value: 'Europe/Copenhagen' }}
                                    options={[
                                      { value: 'Europe/Copenhagen', label: 'Europe/Copenhagen' },
                                      { value: 'UTC', label: 'UTC' }
                                    ]}
                                    name='TimeZone'
                                    description='TimeZone'
                                  />
                                ) : (
                                  TimeZone
                                ),
                                'Created At': (
                                  <DisplayDate>{CreatedAt}</DisplayDate>
                                ),
                                Comment: isEditMode ? (
                                  <FormikInput
                                    key='Comment'
                                    type='textarea'
                                    name='Comment'
                                    description='Comment'
                                  />
                                ) : (
                                  Comment ?? ''
                                )
                              }
                            ]}
                            hasWideDescriptions
                          />
                        )
                      }
                    },
                    {
                      tab: {
                        content: (
                          <IconWithText
                            iconName='address-book'
                            text='Parameters'
                          />
                        )
                      },
                      panel: {
                        content: (
                          <DataDefinitionList
                            classNames={['border-0', 'detail-page-list']}
                            listData={this.mapParameters(isEditMode)}
                            hasWideDescriptions
                          />
                        )
                      }
                    },
                    {
                      tab: {
                        content: (
                          <IconWithText
                            iconName='address-book'
                            text='Disables'
                          />
                        )
                      },
                      panel: {
                        content: (
                          <DataDefinitionList
                            classNames={['border-0', 'detail-page-list']}
                            listData={[
                              {
                                Disables: (
                                  <div>
                                    {isEditMode ? (
                                      <EditDisables />
                                    ) : (
                                      this.showDisables(Disables)
                                    )}
                                  </div>
                                )
                              }
                            ]}
                            hasWideDescriptions
                          />
                        )
                      }
                    }
                  ]}
                </Tabs>
              </React.Fragment>
            )}
          </EditableArea>
        ) : (
          ''
        )}
      </React.Fragment>
    );
  }
}

const Integration = withRouter(
  withApi(
    connect(({ user }) => ({ isLoggedIn: !!user }), {})(IntegrationInternal)
  )
);

Integration.requiresLogin = true;
Integration.propTypes = { isLoggedIn: PropTypes.bool };

export default Integration;